import { getEmbedApiContext } from '../api/context';

/**
 * Browser-side client for the BrainServer custom-TCP TTS route. The
 * browser POSTs only `{ text }`; admin-api opens a TCP socket from
 * Lambda to the persisted `voice.tts.brainserver.{host, port}`, runs
 * the 0x07 round-trip, normalises any IEEE-float WAV the engine emits
 * to PCM-16, and streams the WAV back as `Content-Type: audio/wav`.
 *
 * The browser NEVER sees the upstream host/port — that lives on the
 * character record and is read server-side. Mirrors the voai-client
 * pattern (character-keyed proxy + character-record sealed secrets).
 */

/** Server-side text cap (mirrors the lambda handler's MAX_TEXT_CHARS). */
const BRAINSERVER_CHUNK_LIMIT = 1000;

export interface BrainserverSynthesizePayload {
  text: string;
  signal?: AbortSignal;
}

export async function synthesizeBrainserverBlob(
  characterId: string,
  payload: BrainserverSynthesizePayload,
): Promise<Blob> {
  const ctx = getEmbedApiContext();
  let text = payload.text;
  if (text.length > BRAINSERVER_CHUNK_LIMIT) {
    console.warn(`[brainserver] chunk exceeds 1000-char limit (${text.length}), truncating`);
    text = text.slice(0, BRAINSERVER_CHUNK_LIMIT);
  }
  const res = await fetch(
    ctx.apiUrl(`/api/characters/${encodeURIComponent(characterId)}/voice/tts/brainserver`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...ctx.apiAuthHeaders() },
      body: JSON.stringify({ text }),
      signal: payload.signal,
    },
  );
  if (!res.ok) {
    const errBody = await safeBody(res);
    throw new Error(`BrainServer /voice/tts/brainserver HTTP ${res.status}: ${errBody}`);
  }
  return await res.blob();
}

async function safeBody(res: Response): Promise<string> {
  try { return (await res.text()).slice(0, 500); }
  catch { return '<unreadable>'; }
}

export { BRAINSERVER_CHUNK_LIMIT };
